/** @jsxImportSource @emotion/react */

import { Typography } from "@a_team/ui-components";
import {
  CSSRulesResolver,
  useCSSRulesWithTheme,
} from "hooks/useCSSRulesWithTheme";
import { useEffect, useMemo, useState } from "react";
import Stars from "components/Stars";
import { useReview } from "queries/userReviews/useReview";
import Skeleton from "react-loading-skeleton";
import { useUpdateUserReview } from "queries/userReviews/useUpdateUserReview";
import useQueryParams from "hooks/useQueryParams";
import analytics from "analytics";
import { BUTTON_CLICKED, STAR_RATING_CLICKED } from "analytics/events";
import {
  Feedback,
  FEEDBACK_MAX,
  FEEDBACK_MIN,
} from "views/UserReviewV2/steps/Feedback/Feedback";
import { Actions } from "views/UserReviewV2/steps/Actions";
import { useHistory } from "react-router-dom";

interface Props {
  reviewId?: string;
  onNext: () => void;
}

const getCSSRules: CSSRulesResolver = ({ breakpoints, colors }) => ({
  submit: {
    padding: "10px 40px",
    alignSelf: "end",
    width: "100%",
    [`@media (min-width: ${breakpoints.sm}px)`]: {
      width: "unset",
    },
  },
  required: {
    color: colors.Red[600],
  },
  button: {
    width: 175,
    padding: "10px 24px",
    height: 40,
  },
  icon: {
    transform: "rotate(180deg) scaleX(-1)",
  },
});

export const STARS = [
  {
    value: 1,
    label: "Poor",
  },
  {
    value: 2,
    label: "Not good",
  },
  {
    value: 3,
    label: "Okay",
  },
  {
    value: 4,
    label: "Good",
  },
  {
    value: 5,
    label: "Great",
  },
];

export const StarRating = (props: Props) => {
  const { onNext, reviewId } = props;
  const styles = useCSSRulesWithTheme(getCSSRules);
  const { data: review } = useReview(reviewId);
  const { rating: ratingParam } = useQueryParams<{ rating?: number }>();
  const { mutateAsync, isLoading } = useUpdateUserReview(reviewId);
  const history = useHistory();

  const showPublicFeedback = !!review?.starRating && review.starRating > 3;

  const [privateFeedback, setPrivateFeedback] = useState(
    review?.privateFeedback || ""
  );
  const [publicFeedback, setPublicFeedback] = useState(
    review?.publicFeedback || ""
  );

  const [endedRoleReasons, setEndedRoleReasons] = useState(
    review?.endedRoleReasons || []
  );

  const [starRating, setStarRating] = useState<number | null>(
    review?.starRating || null
  );

  const handleStarClick = async (value: number) => {
    setStarRating(value);

    await mutateAsync({
      starRating: value,
    });
    analytics.track(STAR_RATING_CLICKED, { value });
  };

  useEffect(() => {
    if (review && ratingParam) {
      if (Number.isNaN(ratingParam)) {
        return;
      }
      const parsedRating = Number(ratingParam);

      if (parsedRating < 1 || parsedRating > 5) {
        return;
      }

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("rating");
      history.replace({
        search: searchParams.toString(),
      });

      mutateAsync({
        starRating: parsedRating,
      });
    }
  }, [ratingParam, history, review]);

  useEffect(() => {
    let updateRating = review?.starRating;
    if (ratingParam) {
      updateRating = Number(ratingParam);
    }
    if (updateRating) {
      setStarRating(updateRating);
    }
  }, [ratingParam, review?.starRating]);

  const handleUpdateReview = async () => {
    if (!review || !starRating) {
      return Promise.reject();
    }
    await mutateAsync({
      starRating,
      publicFeedback:
        publicFeedback.trim().length === 0 ? undefined : publicFeedback,
      privateFeedback:
        privateFeedback.trim().length === 0 ? undefined : privateFeedback,
      isCompleted: true,
    });
    onNext();
    analytics.track(BUTTON_CLICKED, {
      name: "Builder Ratings - Star Rating Submit",
      value: starRating,
    });
  };

  const submitButtonDisabled = useMemo(() => {
    if (!review || starRating === null) {
      return true;
    }

    if (starRating > 3) {
      const publicFeedbackLength = publicFeedback.trim().length;

      const publicFeedbackInvalid =
        (publicFeedbackLength > 0 && publicFeedbackLength < FEEDBACK_MIN) ||
        publicFeedbackLength > FEEDBACK_MAX;

      if (publicFeedbackInvalid) {
        return true;
      }
    } else {
      const privateFeedbackLength = privateFeedback.trim().length;
      const privateFeedbackInvalid =
        (privateFeedbackLength > 0 && privateFeedbackLength < FEEDBACK_MIN) ||
        privateFeedbackLength > FEEDBACK_MAX;

      return privateFeedbackInvalid;
    }
  }, [review, starRating, privateFeedback, publicFeedback]);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {review ? (
        <>
          <Typography
            variant="textLarge"
            component="div"
            size={16}
            weight={"bold"}
            style={{ display: "flex", alignItems: "center" }}
          >
            How was {review?.toUser?.firstName || "this builder"}’s overall work
            on your team?<span css={styles.required}>*</span>
          </Typography>
          <Typography
            variant="textMedium"
            weight={"regular"}
            size={14}
            style={{ marginTop: 4 }}
            color={"Grey@600"}
          >
            Please rate your experience working with this builder. The star
            rating will not be shared with the builder.
          </Typography>

          <div
            style={{
              margin: "24px 0",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Stars
              items={STARS}
              onSelect={handleStarClick}
              selectedValue={starRating}
              disabled={!review || isLoading}
              loading={!review}
            />
          </div>

          {starRating !== null && (
            <>
              <Feedback
                review={review}
                isLoading={!!isLoading}
                showPublicFeedback={showPublicFeedback}
                privateFeedback={privateFeedback}
                setPrivateFeedback={setPrivateFeedback}
                publicFeedback={publicFeedback}
                setPublicFeedback={setPublicFeedback}
                endedRoleReasons={endedRoleReasons}
                setEndedRoleReasons={setEndedRoleReasons}
              />

              <Actions
                isSubmitting={isLoading}
                isLoading={!review}
                onNext={handleUpdateReview}
                submitButtonDisabled={submitButtonDisabled}
                submitText={"Submit"}
              />
            </>
          )}
        </>
      ) : (
        <>
          <Skeleton height={20} width={300} />
          <Skeleton height={10} width={400} />
        </>
      )}
    </div>
  );
};
